import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, X } from "lucide-react";

const emptyPerson = () => ({ name: "", email: "" });

export default function NewProject() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "", address: "", description: "",
    start_date: "", target_end_date: "",
    customers: [emptyPerson()],
  });
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const updCustomer = (idx, key, val) => {
    setF((s) => ({ ...s, customers: s.customers.map((p, i) => (i === idx ? { ...p, [key]: val } : p)) }));
  };
  const addCustomer = () => setF((s) => ({ ...s, customers: [...s.customers, emptyPerson()] }));
  const removeCustomer = (idx) =>
    setF((s) => ({ ...s, customers: s.customers.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    const customers = f.customers
      .map((c) => ({ name: c.name.trim(), email: c.email.trim().toLowerCase() }))
      .filter((c) => c.name && c.email);
    if (customers.length === 0) { toast.error("Add at least one home owner"); return; }
    setLoading(true);
    try {
      const payload = {
        name: f.name,
        address: f.address,
        description: f.description || null,
        customers,
        contractors: [],
        start_date: f.start_date || null,
        target_end_date: f.target_end_date || null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success("Project created. Invitations sent.");
      navigate(`/projects/${data.project.project_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto py-8">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[#7A7571] hover:text-[#2C2A29] mb-6">
        <ArrowLeft size={16} /> Back
      </button>
      <h1 className="font-display text-4xl font-bold text-[#2C2A29] mb-2">New project</h1>
      <p className="text-[#7A7571] mb-8">Set up your project hub. You&apos;ll be able to add contractors from your directory after creating it.</p>

      <form onSubmit={submit} className="space-y-6">
        {/* Basics */}
        <div className="card-soft space-y-4">
          <Section>Basics</Section>
          <Field label="Project name" testId="np-name" value={f.name} onChange={(v) => upd("name", v)} placeholder="Tanglin Halt Reno" />
          <Field label="Address" testId="np-address" value={f.address} onChange={(v) => upd("address", v)} placeholder="123 Tanglin Halt Rd" />
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Description <span className="text-[#7A7571] normal-case tracking-normal font-normal">(optional)</span></label>
            <textarea data-testid="np-description" className="input-soft min-h-[90px]"
              value={f.description} onChange={(e) => upd("description", e.target.value)}
              placeholder="Full home renovation — 4-room HDB, hacking, wet works, carpentry." />
          </div>
          <div className="rounded-2xl bg-[#F7F4EC] border border-[#EBE5D9] p-3 text-xs text-[#7A7571] leading-relaxed">
            <span className="font-semibold text-[#2C2A29]">Contract value</span> is auto-calculated from the sum of task line-items after you import your quotation or add tasks manually. VO-generated tasks are excluded.
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start" testId="np-start" type="date" value={f.start_date} onChange={(v) => upd("start_date", v)} required={false} />
            <Field label="Target end" testId="np-end" type="date" value={f.target_end_date} onChange={(v) => upd("target_end_date", v)} required={false} min={f.start_date || undefined} />
          </div>
        </div>

        {/* Customers */}
        <div className="card-soft space-y-4">
          <div className="flex items-center justify-between">
            <Section>Home owners</Section>
            <button type="button" onClick={addCustomer}
              data-testid="np-add-customer"
              className="btn-secondary text-sm !py-1.5 !px-3 inline-flex items-center gap-1">
              <Plus size={14} /> Add another
            </button>
          </div>
          {f.customers.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`np-customer-row-${i}`}>
              <Field label="Name" testId={`np-customer-name-${i}`} value={c.name} onChange={(v) => updCustomer(i, "name", v)} placeholder="Alex Lim" />
              <Field label="Email (Google)" testId={`np-customer-email-${i}`} type="email" value={c.email} onChange={(v) => updCustomer(i, "email", v)} placeholder="alex@gmail.com" />
              {f.customers.length > 1 && (
                <button type="button" onClick={() => removeCustomer(i)}
                  data-testid={`np-customer-remove-${i}`}
                  className="btn-ghost !p-2 mb-1 self-center" title="Remove">
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>

        <button data-testid="np-submit" disabled={loading} className="btn-primary w-full">
          {loading ? "Creating…" : "Create project"}
        </button>
      </form>
    </div>
  );
}

function Section({ children }) {
  return <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{children}</div>;
}

function Field({ label, testId, value, onChange, type = "text", placeholder, required = true, min }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">{label}</label>
      <input data-testid={testId} type={type} required={required} value={value}
        onChange={(e) => onChange(e.target.value)} className="input-soft" placeholder={placeholder} min={min} />
    </div>
  );
}
